import React, { useEffect, useRef, useState, useCallback } from 'react';

import styles from './masonry.module.scss';

interface MasonryProps {
  children: React.ReactNode;
  columns?: {
    default: number;
    tablet: number;
    mobile: number;
  };
  gap?: number;
  ariaLabel?: string;
}

export default function Masonry({
  children,
  columns = { default: 3, tablet: 2, mobile: 1 },
  gap = 20,
}: MasonryProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(columns.default);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const updateColumns = useCallback(() => {
    const width = window.innerWidth;
    if (width <= 480) {
      setColumnCount(columns.mobile);
    } else if (width <= 768) {
      setColumnCount(columns.tablet);
    } else {
      setColumnCount(columns.default);
    }
  }, [columns]);

  useEffect(() => {
    if (!mounted) return;

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, [mounted, updateColumns]);

  const childrenArray = React.Children.toArray(children);

  if (!mounted) {
    const columnsArray: React.ReactNode[][] = Array.from({ length: columns.default }, () => []);
    childrenArray.forEach((child, index) => {
      columnsArray[index % columns.default].push(child);
    });

    return (
      <section
        ref={containerRef}
        className={styles.masonry}
        style={
          {
            '--gap': `${gap}px`,
            '--columns': columns.default,
          } as React.CSSProperties
        }
      >
        {columnsArray.map((column, columnIndex) => (
          <div
            key={columnIndex}
            className={styles.column}
            role="group"
            aria-label={`Column ${columnIndex + 1}`}
          >
            {column.map((child, childIndex) => (
              <div key={childIndex} className={styles.item}>
                {child}
              </div>
            ))}
          </div>
        ))}
      </section>
    );
  }

  const columnsArray: React.ReactNode[][] = Array.from({ length: columnCount }, () => []);

  childrenArray.forEach((child, index) => {
    const targetColumn = index % columnCount;
    columnsArray[targetColumn].push(child);
  });

  return (
    <section
      ref={containerRef}
      className={styles.masonry}
      style={{ '--gap': `${gap}px`, '--columns': columnCount } as React.CSSProperties}
    >
      {columnsArray.map((column, columnIndex) => (
        <div
          key={columnIndex}
          className={styles.column}
          role="group"
          aria-label={`Column ${columnIndex + 1}`}
        >
          {column.map((child, childIndex) => (
            <div key={`${columnIndex}-${childIndex}`} className={styles.item}>
              {child}
            </div>
          ))}
        </div>
      ))}
    </section>
  );
}
